import { Table, Thead, Th, Tbody, Tr, Td, Spinner, Button, HStack, Checkbox, Wrap, WrapItem, Tooltip, Flex } from "@chakra-ui/react";
import { FaPencil } from "react-icons/fa6";
import moment, { MomentInput } from "moment";
import React, { ReactElement, useCallback, useContext, useMemo, useRef, useState } from "react";
import { TPageTableItem } from "../pages/ebooks-page/types";
import { TSection, TSectionTableProps } from "./section-table.config";
import SortableIcon from "./sortable-icon";
import State, { StateType } from "./state";
import UserContext from "../context/user-context";

export type { TSection, TSectionTableProps };

const FADE_DURATION_MS = 200;

const OverflowTooltip = ({ label, children }: { label: string; children: ReactElement }): ReactElement => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const ref = useCallback((node: HTMLElement | null) => {
    if (node) {
      setIsOverflowing(node.scrollWidth > node.clientWidth);
    }
  }, []);

  return (
    <Tooltip label={label} openDelay={100} placement="top" hasArrow isDisabled={!isOverflowing}>
      {React.cloneElement(children, { ref })}
    </Tooltip>
  );
};

const CellContent = <T,>({ column, item }: { column: TPageTableItem<T>; item: T }): ReactElement => {
  const raw = item[column.key as keyof T];
  const value = column.valueTransform ? column.valueTransform(raw, item) : raw;

  if (column.type === "date") return <>{moment(value as MomentInput).format("DD.MM.YY")}</>;
  if (column.type === "image") return <img src={`${value}`} height="60px" width="40px" alt="" />;
  if (column.type === "state") {
    return <State value={value as StateType} link={column.link ? column.link(item) : ""} />;
  }
  return <>{(value as string | number) ?? ""}</>;
};

const SectionTable = <T,>({
  staticColumns,
  sections,
  data,
  isLoading,
  error,
  sort,
  handleSort,
  rowKey,
  onRowClick,
  onCellClick,
  clickableColumns,
  activeSection: controlledSection,
  onSectionChange,
  hiddenColumns: controlledHidden,
  onToggleColumn,
  editedRows,
  editedCells,
  onEditRow,
  actions,
}: TSectionTableProps<T>): ReactElement => {
  const user = useContext(UserContext);
  const visibleSections = useMemo(() =>
    sections.filter((s) => !s.roles?.length || s.roles.some((role) => user?.roles.includes(role))),
    [sections, user?.roles],
  );

  const [internalSection, setInternalSection] = useState(visibleSections[0]?.identifier ?? "");
  const [fading, setFading] = useState(false);
  const pendingRef = useRef<string | null>(null);
  const [internalHidden, setInternalHidden] = useState<Set<string>>(new Set());

  const hiddenColumns = controlledHidden ?? internalHidden;
  const toggleColumn = onToggleColumn ?? ((key: string) => {
    setInternalHidden((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  });

  const requestedSection = controlledSection ?? internalSection;
  const activeSection = visibleSections.some((s) => s.identifier === requestedSection)
    ? requestedSection
    : visibleSections[0]?.identifier ?? "";
  const activeSectionDef = visibleSections.find((s) => s.identifier === activeSection);
  const hasRole = (roles: string[]) =>
    !roles.length || roles.some((role) => user?.roles.includes(role));
  const allDynamic = (activeSectionDef?.columns ?? []).filter((col) => hasRole(col.roles));
  const visibleStatic = staticColumns.filter((col) => !hiddenColumns.has(col.id ?? col.key) && hasRole(col.roles));
  const totalColumns = visibleStatic.length + allDynamic.length + (onEditRow ? 1 : 0);

  const stickyOffsets: undefined[] = visibleStatic.map(() => undefined);

  const dynamicStyle = {
    transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
    opacity: fading ? 0 : 1,
  };

  const switchSection = (identifier: string): void => {
    if (identifier === activeSection) return;
    setFading(true);
    pendingRef.current = identifier;
    setTimeout(() => {
      (onSectionChange ?? setInternalSection)(pendingRef.current!);
      setFading(false);
      pendingRef.current = null;
    }, FADE_DURATION_MS);
  };

  const isCellClickable = (columnKey: string): boolean =>
    !!onCellClick && (!clickableColumns || clickableColumns.includes(columnKey));

  const handleCellClick = (item: T, columnKey: string): void => {
    if (isCellClickable(columnKey)) {
      onCellClick!(item, columnKey);
    }
  };

  const stickyStyle = (leftPx?: number): object =>
    leftPx !== undefined
      ? { position: "sticky" as const, left: leftPx, zIndex: 2, backgroundColor: "white" }
      : {};

  const renderCell = (column: TPageTableItem<T>, item: T, extraStyle?: object, sticky?: number, itemKey?: string) => {
    const uid = column.id ?? column.key;
    const clickable = isCellClickable(column.key) || !!column.onClick;
    const isCellEdited = itemKey ? editedCells?.get(itemKey)?.has(uid) : false;
    const cellStyle = { ...column.style, ...stickyStyle(sticky), ...extraStyle };
    const onClickHandler = column.onClick
      ? () => column.onClick!(item)
      : clickable
        ? () => handleCellClick(item, column.key)
        : undefined;
    const tooltipValue = column.tooltipTransform
      ? column.tooltipTransform(item[column.key as keyof T], item)
      : column.tooltip
        ? String(item[column.key as keyof T] ?? "")
        : undefined;
    const content = column.ellipsis ? (
      <div style={{ maxWidth: column.ellipsis, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        <CellContent column={column} item={item} />
      </div>
    ) : column.lineClamp ? (
      <div style={{
        display: "-webkit-box",
        WebkitLineClamp: column.lineClamp,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }}>
        <CellContent column={column} item={item} />
      </div>
    ) : (
      <CellContent column={column} item={item} />
    );
    const wrappedContent = column.tooltipTransform && tooltipValue ? (
      <Tooltip label={tooltipValue} openDelay={100} placement="top" hasArrow>
        <span>{content}</span>
      </Tooltip>
    ) : tooltipValue ? (
      <OverflowTooltip label={tooltipValue}>
        <span>{content}</span>
      </OverflowTooltip>
    ) : content;
    const cell = (
      <Td
        key={uid}
        style={cellStyle}
        onClick={onClickHandler}
        cursor={clickable ? "pointer" : undefined}
        bg={isCellEdited ? "green.100" : undefined}
        _hover={clickable ? { backgroundColor: "gray.100" } : undefined}
        textDecoration={column.onClick ? "underline" : undefined}
      >
        {wrappedContent}
      </Td>
    );

    return cell;
  };

  const renderHeaderCell = (column: TPageTableItem<T>, extraStyle?: object, sticky?: number) => {
    const uid = column.id ?? column.key;
    const cellStyle = {
      whiteSpace: "normal" as const,
      verticalAlign: "bottom" as const,
      ...column.style,
      ...stickyStyle(sticky),
      ...extraStyle,
    };
    if (sticky !== undefined) {
      (cellStyle as Record<string, unknown>).zIndex = 3;
    }
    const cell = (
      <Th
        key={uid}
        style={cellStyle}
        onClick={() => column.sortable && handleSort(column.sortKey ?? column.key)}
        cursor={column.sortable ? "pointer" : undefined}
        title={column.label}
      >
        {column.label}
        <SortableIcon sort={sort} column={column} />
      </Th>
    );

    return cell;
  };

  return (
    <>
      <Flex justify="space-between" align="center">
      <HStack
        spacing={1}
        bg="gray.100"
        p="3px"
        borderRadius="lg"
        width="fit-content"
      >
        {visibleSections.map((section) => {
          const isActive = section.identifier === activeSection;
          return (
            <Button
              key={section.identifier}
              variant="unstyled"
              size="sm"
              px={5}
              height="30px"
              fontSize="xs"
              fontWeight={isActive ? "600" : "500"}
              letterSpacing="0.02em"
              textTransform="uppercase"
              color={isActive ? "gray.800" : "gray.500"}
              bg={isActive ? "white" : "transparent"}
              boxShadow={isActive ? "sm" : "none"}
              borderRadius="md"
              transition="all 0.2s ease"
              _hover={isActive ? {} : { color: "gray.700", bg: "gray.50" }}
              onClick={() => switchSection(section.identifier)}
            >
              {section.label}
            </Button>
          );
        })}
      </HStack>
      {actions}
      </Flex>
      <Wrap spacing={3} px={2} py={1} align="center" minH="32px">
        {staticColumns.map((col) => {
          const uid = col.id ?? col.key;
          return (
            <WrapItem key={uid}>
              <Checkbox
                size="sm"
                isChecked={!hiddenColumns.has(uid)}
                onChange={() => toggleColumn(uid)}
              >
                {col.label}
              </Checkbox>
            </WrapItem>
          );
        })}
      </Wrap>
      <Table size="sm" style={{ width: "100%", tableLayout: "fixed" }}>
        <colgroup>
          {visibleStatic.map((col) => {
            const s = col.style as Record<string, unknown> | undefined;
            return <col key={col.id ?? col.key} style={{ width: String(s?.width ?? "auto") }} />;
          })}
          {allDynamic.map((col) => (
            <col key={col.id ?? col.key} />
          ))}
          {onEditRow && <col style={{ width: "50px" }} />}
        </colgroup>
        <Thead>
          <Tr style={{ height: "52px" }}>
            {visibleStatic.map((col, i) => renderHeaderCell(col, undefined, stickyOffsets[i]))}
            {allDynamic.map((col) => renderHeaderCell(col, dynamicStyle))}
            {onEditRow && <Th style={{ width: "50px" }} />}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading && (
            <Tr>
              <Td colSpan={totalColumns} textAlign="center"><Spinner /></Td>
            </Tr>
          )}
          {!!error && (
            <Tr>
              <Td colSpan={totalColumns}>Error loading data</Td>
            </Tr>
          )}
          {!isLoading && !error && data?.map((item) => {
            const itemKey = String(item[rowKey]);
            return (
            <Tr
              key={itemKey}
              onClick={onRowClick ? () => onRowClick(item) : undefined}
              cursor={onRowClick ? "pointer" : undefined}
            >
              {visibleStatic.map((col, i) => renderCell(col, item, undefined, stickyOffsets[i], itemKey))}
              {allDynamic.map((col) => renderCell(col, item, dynamicStyle, undefined, itemKey))}
              {onEditRow && (
                <Td style={{ width: "50px", textAlign: "center", padding: "4px" }}>
                  <HStack gap={2} flexWrap="nowrap" justifyContent="center">
                    <Button size="sm" onClick={(e) => { e.stopPropagation(); onEditRow(item); }}>
                      <FaPencil size="12" />
                    </Button>
                  </HStack>
                </Td>
              )}
            </Tr>
          );})}
        </Tbody>
      </Table>
    </>
  );
};

export default SectionTable;
